import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { VoteResultsOption } from '../../../models/voteResultsOption';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { ColumnConfig, LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-vote-results-option-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './vote-results-option-list.component.html',
  styleUrl: './vote-results-option-list.component.scss'
})
export class VoteResultsOptionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  voteResultsOptions = signal<VoteResultsOption[]>([]);

  voteResultsOptionsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true },
    { key: 'type', header: 'Type', type: 'select', sortable: true, editable: true, selectOptions: [
      { value: 1, label: 'Board vote recommendations' },
      { value: 2, label: 'Decided By' },
      { value: 3, label: 'Meeting Disclosure Status' },
      { value: 4, label: 'Meeting Type' },
      { value: 5, label: 'Shareholder Proposal' },
      { value: 6, label: 'Vote Requirement' },
      { value: 7, label: 'Vote Result' }
    ] }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadVoteResultsOptions();
  }

  loadVoteResultsOptions(): void {
    this.spinnerService.show();
    this.lutService.getVoteResultsOptions().subscribe({
      next: (data) => {
        const sortedData = this.sortVoteResultsOptions(data);
        this.voteResultsOptions.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading Vote Results Options:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortVoteResultsOptions(options: VoteResultsOption[]): VoteResultsOption[] {
    return [...options].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateVoteResultsOption(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(voteResultsOption: VoteResultsOption): void {
  }

  onEditVoteResultsOption(voteResultsOption: VoteResultsOption): void {
  }

  onDeleteVoteResultsOption(voteResultsOption: VoteResultsOption): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(voteResultsOption.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalOptions = this.voteResultsOptions();

          // 1. Optimistically remove from local state immediately
          this.voteResultsOptions.update(options => options.filter(c => c.id !== voteResultsOption.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteVoteResultsOption(voteResultsOption.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Vote Results Option deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting Vote Results Option:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.voteResultsOptions.set(originalOptions);
              this.toast.error(`<strong>Failed to delete Vote Results Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: VoteResultsOption, newName: string} | {item: VoteResultsOption, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: VoteResultsOption;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.voteResultsOptions.update(options => {
      const updated = options.map(c => c.id === event.item.id ? updatedItem : c);
      return this.sortVoteResultsOptions(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateVoteResultsOption(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Vote Results Option updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.voteResultsOptions.update(options => {
          const updated = options.map(c => c.id === event.item.id ? response : c);
          return this.sortVoteResultsOptions(updated);
        });
      },
      error: (error) => {
        console.error('Error updating Vote Results Option:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.voteResultsOptions.update(options =>
          options.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update Vote Results Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newOption: VoteResultsOption = {
      id: tempId,
      name: event['name'],
      type: event['type'] ?? 1,
      systemReserved: false
    };

    // 1. Optimistically add to local state immediately and sort
    this.voteResultsOptions.update(options => this.sortVoteResultsOptions([...options, newOption]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createVoteResultsOption({
      name: event['name'],
      type: event['type'] ?? 1,
      systemReserved: false
    }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Vote Results Option created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.voteResultsOptions.update(options => {
          const updated = options.map(c => c.id === tempId ? response : c);
          return this.sortVoteResultsOptions(updated);
        });
      },
      error: (error) => {
        console.error('Error creating Vote Results Option:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.voteResultsOptions.update(options =>
          options.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Vote Results Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
